import React, { useState, useEffect, useCallback } from 'react';

import helpers from 'helpers';

import { letterMapper, AnnotationTooltip } from 'helpers/annotations';
import { renderUsefulLines, isLineUseful } from './utils';
import { useBatch, BATCH_TYPES } from './BatchContext';

const ContentAnnotations = ({ 
    textLetter, 
    annotations,
    predictorData,
    showUsefulLines,
    lineScoreData,
}) => {
    const [characters, setCharacters] = useState();
    const [tooltips, setTooltips] = useState();

    const { addToBatch, removeFromBatch, data: batchData } = useBatch();

    const onLineClick = useCallback((ev, line, matchedData) => {
        if (!matchedData) return;

        const trimmedLine = line.trim();
        
        const originalIsUseful = isLineUseful(matchedData);

        const batchType = originalIsUseful 
            ? BATCH_TYPES.USEFUL_TO_NOT_USEFUL 
            : BATCH_TYPES.NOT_USEFUL_TO_USEFUL;

        const batchArray = originalIsUseful 
            ? batchData.usefulToNotUseful 
            : batchData.notUsefulToUseful;
        const normalizedLine = trimmedLine.toLowerCase();
        const existingItem = batchArray.find(item => item.line.trim().toLowerCase() === normalizedLine);

        if (existingItem) {
            removeFromBatch(existingItem.id, batchType);
        } else {
            addToBatch(line, batchType);
        }
    }, [addToBatch, removeFromBatch, batchData]);

    const createCharacters = (textLetter, annotations) => {
        let characters = textLetter.split('');

        characters = characters.map((symbol, symbolPosition) => {
            symbol = symbol === ' ' ? '\u00A0' : symbol;

            return {
                symbol,
                symbolPosition,
                style: {},
                ids: [],
                tooltipContent: [],
            };
        });

        const sortedAnnotations = [...annotations].sort((a, b) => {
            if (a.priority > b.priority) {
                return -1;
            }

            if (a.priority < b.priority) {
                return 1;
            }

            return 0;
        });

        for (let i = 0; i < sortedAnnotations.length; i++) {
            const hashColor = helpers.createHashColor();

            for (let j = sortedAnnotations[i].start; j < sortedAnnotations[i].end; j++) {
                if (sortedAnnotations[i].styles.background === '') {
                    characters[j].style = {
                        ...sortedAnnotations[i].styles,
                        background: hashColor,
                    };
                } else {
                    characters[j].style = sortedAnnotations[i].styles;
                }

                characters[j].ids.push(sortedAnnotations[i].id);
                characters[j].tooltipContent.push({
                    rule: sortedAnnotations[i].features.rule,
                    type: sortedAnnotations[i].type,
                });
            }
        }

        return characters;
    };

    const createTooltips = (characters) => {
        const map = new Map();
        characters.forEach((char) => {
            map.set(char.ids.join(''), char);
        });
        return Array.from(map.values());
    };

    useEffect(() => {
        if (annotations.length > 0) {
            const newCharacters = createCharacters(textLetter, annotations);
            const newTooltips = createTooltips(newCharacters);

            setCharacters(newCharacters);
            setTooltips(newTooltips);
        } else {
            setCharacters(undefined);
            setTooltips(undefined);
        }
    }, [textLetter, annotations]);

    return (
        <div>
            <div className='letter-text'>
                {
                    (() => {
                        if (showUsefulLines) {
                            return renderUsefulLines(
                                predictorData, 
                                textLetter, 
                                onLineClick, 
                                lineScoreData,
                                batchData,
                            );
                        }
                        if (characters) {
                            return Array.from(letterMapper(characters));
                        }
                        return textLetter;
                    })()
                }
            </div>
            <AnnotationTooltip
                tooltips={tooltips}
                renderTooltip={tooltip => (
                    <div className='tooltip-row'>
                        {
                            tooltip.tooltipContent.map((t, k) => {
                                const rule = t.rule && `Rule - ${t.rule}, `;

                                return (
                                    <div key={k} className='tooltip-column'>
                                        <div className='col'>{rule}</div>
                                        <div className='col'>{`Type - ${t.type}`}</div>
                                    </div>
                                );
                            })
                        }
                    </div>
                )}
            />
        </div>
    );
};

export default ContentAnnotations;



// WEBPACK FOOTER //
// ./app/components/Letters/Single/Gazetter/Content/Annotations.jsx